import {
	ChangeDetectionStrategy,
	Component,
	computed,
	CUSTOM_ELEMENTS_SCHEMA,
	effect,
	forwardRef,
	inject,
	OnInit,
	signal,
	ViewEncapsulation,
	WritableSignal,
} from '@angular/core';
import { format } from 'fecha';

import { DataFormats } from '@skylab-shared/constants';
import { ConsoleLogger } from '@skylab-shared/core';
import {
	getMonthRange,
	getPreviousDay,
	getStartOfDate,
	isSameDay,
	isEqual,
	isUndefined
} from '@skylab-shared/utils';

import { injectTranslation } from '@skylab-angular-shared/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { DialogInsService } from '../../../dialog/dialog-ins.service';
import { DialogState } from '../../../dialog/dialog.model';
import { SelectValueAccessorDirective } from '../../../value-accessors/select-value-accessor.directive';
import { DatesRangeChip } from '../../filter-chips';
import {
	FILTER_CHIP_STRATEGY,
	FilterChipCollectionItem,
	FilterChipContext,
	FilterChipStrategy
} from '../filter-chip-context';
import { FilterChipHostDirective } from '../filter-chip-host.directive';
import { FilterDateDialogComponent, FilterDateResults, FilterDateData } from './filter-chip-date-range-dialog/filter-date-dialog.component';
import { DateFilterSelectValue, getDateRangeBySelectValue, DateFilterReturnValues, DateFitlerValueRange } from './filter-chip-date-utils';

import { IconComponent } from '../../../icon/icon.component';

@Component({
	selector: 'skl-date-range-filter-chip',
	templateUrl: './filter-chip-date-range.component.html',
	hostDirectives: [{ 
		directive: FilterChipHostDirective, 
		inputs: ['chip'] 
	}],
	host: {
		'[class.has-value]': 'ctx.hasValue()'
	},
	providers: [
		FilterChipContext,
		{ provide: FILTER_CHIP_STRATEGY, useExisting: forwardRef(() => DateRangeFilterChip) }
	],
	imports: [SelectValueAccessorDirective, FormsModule, TranslatePipe, IconComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeFilterChip implements OnInit, FilterChipStrategy {
	protected readonly _T = injectTranslation();
	protected readonly ctx = inject(FilterChipContext);
	readonly #host = inject(FilterChipHostDirective);
	readonly #dialogService = inject(DialogInsService);
	readonly #logger = new ConsoleLogger('DateRangeFilterChip');

	#dialogData: DateFilterReturnValues | null = null;
	#isProcessing = false;

	readonly chosenValues: DateFilterSelectValue[] = [DateFilterSelectValue.Chosen];

	protected readonly chip = computed(() => this.#host.chip() as DatesRangeChip);
	protected readonly value = this.ctx.value as WritableSignal<DateFilterReturnValues | null>;
	protected readonly collection = this.ctx.collection;

	protected readonly selectValue = signal<unknown | null>(null);

	protected get converter() { 
		return this.ctx.converter;
	}

	constructor() {
		effect(() => {
			const chip = this.#host.chip();
			if (chip) this.ctx.chip.set(chip);
		});
	}

	public ngOnInit() {
		const chip = this.chip();
		this.ctx.chip.set(chip);

		this.#buildCollection();
		this.ctx.initConverter();
	}

	public setInitialValue() {
		const chip = this.chip();
		if (this.#isValueFromCustomCollection(chip.value)) {
			this.value.set(chip.value);
			this.selectValue.set(chip.value);
			this.ctx.setInitialChipValue(chip.value);
			this.#dialogData = null;
		} else {
			this.ctx.setInitialChipValue(chip.value ? { ...chip.value } : null);
			this.value.set(chip.value);
			if (chip.value === null) {
				this.#dialogData = null;
			} else {
				this.#dialogData = { ...chip.value };
			}
			this.selectValue.set(this.#recognizeDate(chip.value));
		}
	}

	public resetDefaultValue() {
		const chip = this.chip();
		const defaultValue = (!isUndefined(chip.defaultValue) ? chip.defaultValue : chip.initialChipValue) ?? null;
		if (this.#isValueFromCustomCollection(defaultValue)) {
			if (isEqual(defaultValue, this.value())) {
				return;
			}
			this.value.set(defaultValue);
			this.selectValue.set(defaultValue);
			this.#dialogData = null;
			chip.onChange(defaultValue);
		} else {
			if (isEqual(defaultValue, this.value())) {
				return;
			}
			this.value.set(defaultValue);
			if (defaultValue === null) {
				this.#dialogData = null;
			} else {
				this.#dialogData = { ...defaultValue };
			}

			chip.value = defaultValue;
			this.selectValue.set(this.#recognizeDate(defaultValue));
			chip.onChange(defaultValue);
		}
	}

	public onValueChange(value: unknown) {
		if (this.#isProcessing) {
			return;
		}

		this.selectValue.set(value);
		this.#createDialog(value);
	}

	public isSelected(item: FilterChipCollectionItem): boolean {
		return this.selectValue() === item.value;
	}

	public isChosenValue(): boolean {
		return this.selectValue() === DateFilterSelectValue.Chosen && this.getChosenValue() !== '';
	}

	public isChosenItem(item: FilterChipCollectionItem): boolean {
		return item?.value === DateFilterSelectValue.Chosen;
	}

	public getChosenValue(): string {
		if (this.#dialogData?.from && this.#dialogData?.to) {
			const recognized = this.#recognizeDate(this.#dialogData);

			if (recognized === DateFilterSelectValue.Chosen) {
				const from = new Date(this.#dialogData.from);
				const to = new Date(this.#dialogData.to);
				const valueMonthRange = getMonthRange(from);

				if (isSameDay(from, to)) {
					return format(from, DataFormats.date.short);
				} else if (isSameDay(valueMonthRange.firstDay, from) && isSameDay(valueMonthRange.lastDay, to)) {
					return format(from, DataFormats.date.monthYearFormat);
				} else {
					return this._T('ui.filter-chips.date-range.chosen-period', {
						from: format(from, DataFormats.date.short),
						to: format(to, DataFormats.date.short),
					});
				}
			}
		}
		return '';
	}

	public refresh() {
		this.#buildCollection();
	}

	public onOptionClick(item: FilterChipCollectionItem) {
		if (item.value === DateFilterSelectValue.Chosen) {
			this.onValueChange(item.value);
		}
	}

	async #getDateRange(chosenValue: DateFilterSelectValue): Promise<DateFilterReturnValues | null> {
		const chip = this.chip();
		if (chosenValue === DateFilterSelectValue.Chosen) {
			const results = await this.#dialogService.showAsync<{ data: FilterDateData }, FilterDateResults>({
				component: FilterDateDialogComponent,
				componentProps: { 
					data: { 
						title: chip.label, 
						values: this.#dialogData as FilterDateData['values']
					} 
				}
			});
			if (results.state === DialogState.Confirm && results.data) {
				const recognized = this.#recognizeDate({ from: results.data.from, to: results.data.to, chosenValue });
				return { 
					from: results.data.from, 
					to: results.data.to, 
					chosenValue: recognized ?? undefined
				};
			}
			return null;
		}

		const range = getDateRangeBySelectValue(chosenValue) as DateFitlerValueRange | null;
		return range ? { ...range, chosenValue } : null;
	}

	#recognizeDate(value: DateFilterReturnValues | null | undefined): DateFilterSelectValue | null {
		if (!value || isUndefined(value.from) || isUndefined(value.to)) return null;

		const from = getStartOfDate(new Date(value.from));
		const to = getStartOfDate(new Date(value.to));
		if (isNaN(from.getTime()) || isNaN(to.getTime())) return null;

		const isDateInRange = (range: { from: Date; to: Date }) => 
			isSameDay(getStartOfDate(range.from), from) && isSameDay(getStartOfDate(range.to), to);

		if (value.chosenValue && value.chosenValue !== DateFilterSelectValue.Chosen) {
			const range = getDateRangeBySelectValue(value.chosenValue);
			if (range && isDateInRange(range)) {
				return value.chosenValue;
			}
		}

		const currentDate = getStartOfDate(new Date());
		if (isSameDay(from, to)) {
			if (isSameDay(from, currentDate)) return DateFilterSelectValue.CurrentDay;
			if (isSameDay(from, getPreviousDay(currentDate))) return DateFilterSelectValue.PreviousDay;
		}

		for (const key of Object.values(DateFilterSelectValue)) {
			if (key === DateFilterSelectValue.Chosen) continue;
			const range = getDateRangeBySelectValue(key as DateFilterSelectValue);
			if (range && isDateInRange(range)) {
				return key as DateFilterSelectValue;
			}
		}

		return DateFilterSelectValue.Chosen;
	}

	#isValueFromCustomCollection(value: unknown): boolean {
		const chip = this.chip();
		return chip.collection ? chip.collection.find(item => item.value === value) != null : false;
	}

	#getExtraItems(): FilterChipCollectionItem[] {
		return [
			{ 
				value: DateFilterSelectValue.CurrentMonth, 
				label: this._T('ui.filter-chips.date-range.current-month') 
			},
			{ 
				value: DateFilterSelectValue.PreviousMonth, 
				label: this._T('ui.filter-chips.date-range.previous-month') 
			},
			{ 
				value: DateFilterSelectValue.PreviousAndCurrentMonth, 
				label: this._T('ui.filter-chips.date-range.next-and-previous-month') 
			},
			{ 
				value: DateFilterSelectValue.CurrentDay, 
				label: this._T('ui.filter-chips.date-range.current-day') 
			},
			{ 
				value: DateFilterSelectValue.PreviousDay, 
				label: this._T('ui.filter-chips.date-range.previous-day') 
			},
			{ 
				value: DateFilterSelectValue.CurrentYear, 
				label: this._T('ui.filter-chips.date-range.current-year') 
			},
			{ 
				value: DateFilterSelectValue.PreviousYear, 
				label: this._T('ui.filter-chips.date-range.previous-year') 
			},
			{ 
				value: DateFilterSelectValue.Chosen, 
				label: this._T('ui.filter-chips.date-range.chosen') 
			},
		] satisfies FilterChipCollectionItem[];
	}

	#buildCollection() {
		const chip = this.chip();
		const extraItems = this.#getExtraItems();
		if (chip.collection) {
			extraItems.push(...chip.collection);
		}
		this.ctx.buildCollection(extraItems);
		this.setInitialValue();
	}

	async #createDialog(newValue: unknown): Promise<void> {
		const chip = this.chip();
		this.#isProcessing = true;

		try {
			let dateValue: DateFilterReturnValues | null = null;

			if (!this.#isValueFromCustomCollection(newValue)) {
				const selectValue = newValue as DateFilterSelectValue;
				if (selectValue === DateFilterSelectValue.Chosen) {
					const val = this.value();
					this.#dialogData = val ? { from: val.from, to: val.to, chosenValue: val.chosenValue } : null;
				}
				dateValue = await this.#getDateRange(selectValue);
				if (!dateValue && newValue !== null) {
					return;
				}
			} else {
				dateValue = newValue as DateFilterReturnValues;
			}

			if (isEqual(dateValue, this.value())) {
				return;
			}

			this.value.set(dateValue);
			this.#dialogData = dateValue ? { ...dateValue } : null;
			chip.value = dateValue;
			chip.onChange(dateValue);
		} catch (err) {
			this.#logger.error(err);
		} finally {
			this.selectValue.set(this.#recognizeDate(this.value()));
			this.#isProcessing = false;
		}
	}
}
